import { __rest } from "tslib";
import { PLATFORM } from '@lusha/source-platforms';
import { flow, getParent, getRoot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';
import { editedContactAdapter } from '../../../API/adapters';
import { editContactData } from '../../../API/editContactAPI';
import { updateWishedContact } from '../../../API/wishlistApi';
import { notifyFail, notifySuccess } from '../../../hooks/useNotification';
import reportClientEvent from '../../../utils/BIEvents/reportClientEvent';
import { getEditedData } from './utils';
export const actions = self => ({
    setResponseValues(response) {
        const { savedTab } = getRoot(self);
        const contact = getParent(self);
        const contactData = editedContactAdapter(response);
        contact.setContactEditableData(contactData);
        // Sync contact data across to saved tab;
        const contactInSavedTab = savedTab === null || savedTab === void 0 ? void 0 : savedTab.savedContacts.get(response.id);
        if (contactInSavedTab) {
            contactInSavedTab.setContactEditableData(contactData);
        }
    },
    reportContactEditedEvent({ isRevert, itemType, contactId, isBulk, isWishlist = null }) {
        var _a;
        const { context: { url }, userSettings: { platform }, } = getRoot(self);
        const contact = getParent(self);
        reportClientEvent({
            subPlatform: platform,
            pageUrl: url,
            eventType: isRevert ? 'edit_contact_revert_clicked' : 'edit_contact_update_clicked',
            eventValue: itemType,
            product: 'contact_details_update',
            eventExternalKey: contactId,
            eventAdditionalInfo: {
                isBulk,
                contactLinkedinUrl: contact.socialLink,
                companyLinkedinUrl: (_a = contact.company) === null || _a === void 0 ? void 0 : _a.LinkedIn,
                isWishlist,
            },
        });
    },
    editContact: flow(function* editContact(data, isRevert) {
        try {
            const response = yield editContactData(data);
            self.setResponseValues(response);
            notifySuccess(`Contact ${isRevert ? 'reverted' : 'edited'} successfully`);
        }
        catch (error) {
            notifyFail('Failed to edit contact');
        }
    }),
    editWishlistContact: flow(function* editContact(data) {
        try {
            const contact = getParent(self);
            const response = yield updateWishedContact(data);
            contact.setWishlistAddedData(response);
            notifySuccess('Contact edited successfully');
        }
        catch (error) {
            notifyFail('Failed to edit contact');
        }
    }),
    editPartialProfileContact: flow(function* editContact(data, wishlistData, isRevert) {
        try {
            const contact = getParent(self);
            yield editContactData(data);
            // For partial profiles, only update wishlistData (not regular contact data)
            contact.setWishlistAddedData(wishlistData);
            notifySuccess(`Contact ${isRevert ? 'reverted' : 'edited'} successfully`);
        }
        catch (error) {
            notifyFail('Failed to edit contact');
        }
    }),
    setContactKeyBeingEdited(key, value, isManuallyAdded = false, isBulk = false) {
        self.contactKeyBeingEdited = key;
        self.inputValue = value;
        self.originalValue = value;
        self.isManuallyAdded = isManuallyAdded;
        self.isBulk = isBulk;
    },
    setInputValue(value) {
        self.inputValue = value;
    },
    onKeyDown(event) {
        if (event.key === 'Escape') {
            self.setContactKeyBeingEdited(null);
        }
        else if (event.key === 'Enter') {
            self.onEditContact({ isRevert: false });
        }
    },
    onEditContact({ isRevert, contactKey = self.contactKeyBeingEdited, isBulk = self.isBulk }) {
        // We are checking if there is an input error or the input equals the original value
        const contact = getParent(self);
        const inputError = self.inputValue === self.originalValue || self.hasInputValidationError;
        const { app, userSettings } = getRoot(self);
        const { contactId } = contact;
        const _a = getEditedData({
            contact,
            contactKey,
            inputValue: self.inputValue,
            isRevert,
        }), { itemType } = _a, editedData = __rest(_a, ["itemType"]);
        if (!isRevert && inputError) {
            self.setContactKeyBeingEdited(null);
            return;
        }
        if (contact.isWishlistContact) {
            const emails = contact.wishlistData.emails.map(email => ({ value: email.value, type: email.type }));
            const phones = contact.wishlistData.phones.map(phone => ({ value: phone.value, type: phone.type }));
            let result = null;
            let editedEmailsArray = [];
            let editedPhonesArray = [];
            let emailsMetadata = [];
            let phonesMetadata = [];
            switch (self.contactKeyBeingEdited) {
                case 'email1':
                    if (emails[0].value !== self.inputValue) {
                        emails[0].value = self.inputValue;
                        result = { contactId: contact.wishlistData.id, data: { emails, phones } };
                        editedEmailsArray = emails.map(e => e.value);
                        editedPhonesArray = phones.map(p => p.value);
                        emailsMetadata = emails.map(e => ({ isManuallyAdded: true, label: e.type, isEdited: false }));
                        phonesMetadata = phones.map(p => ({ isManuallyAdded: true, type: p.type, isEdited: false }));
                    }
                    break;
                case 'email2':
                    if (emails[1].value !== self.inputValue) {
                        emails[1].value = self.inputValue;
                        result = { contactId: contact.wishlistData.id, data: { emails, phones } };
                        editedEmailsArray = emails.map(e => e.value);
                        editedPhonesArray = phones.map(p => p.value);
                        emailsMetadata = emails.map(e => ({ isManuallyAdded: true, label: e.type, isEdited: false }));
                        phonesMetadata = phones.map(p => ({ isManuallyAdded: true, type: p.type, isEdited: false }));
                    }
                    break;
                case 'phone1':
                    if (phones[0].value !== self.inputValue) {
                        phones[0].value = self.inputValue;
                        result = { contactId: contact.wishlistData.id, data: { emails, phones } };
                        editedEmailsArray = emails.map(e => e.value);
                        editedPhonesArray = phones.map(p => p.value);
                        emailsMetadata = emails.map(e => ({ isManuallyAdded: true, label: e.type, isEdited: false }));
                        phonesMetadata = phones.map(p => ({ isManuallyAdded: true, type: p.type, isEdited: false }));
                    }
                    break;
                case 'phone2':
                    if (phones[1].value !== self.inputValue) {
                        phones[1].value = self.inputValue;
                        result = { contactId: contact.wishlistData.id, data: { emails, phones } };
                        editedEmailsArray = emails.map(e => e.value);
                        editedPhonesArray = phones.map(p => p.value);
                        emailsMetadata = emails.map(e => ({ isManuallyAdded: true, label: e.type, isEdited: false }));
                        phonesMetadata = phones.map(p => ({ isManuallyAdded: true, type: p.type, isEdited: false }));
                    }
                    break;
                default:
                    result = null;
            }
            if (result) {
                if (contact.isPartialProfile) {
                    // For partial profiles, save to /v4/edit-contacts
                    const data = {
                        contactId: contact.contactId,
                        emails: editedEmailsArray,
                        phones: editedPhonesArray,
                        emailsMetadata,
                        phonesMetadata,
                        metadata: {
                            sessionId: app.sessionId || uuid(),
                            isRevert,
                            itemType,
                            platform: PLATFORM.PLUGIN,
                            subPlatform: userSettings.platform,
                        },
                    };
                    self.editPartialProfileContact(data, { emails, phones }, isRevert);
                }
                else {
                    // For non-partial wishlist contacts, use wishlist API
                    self.editWishlistContact(result);
                }
            }
            self.setContactKeyBeingEdited(null);
        }
        else {
            if (!isRevert && inputError) {
                self.setContactKeyBeingEdited(null);
                return;
            }
            const data = Object.assign(Object.assign({ contactId }, editedData), { metadata: {
                    sessionId: app.sessionId || uuid(),
                    isRevert,
                    itemType,
                    platform: PLATFORM.PLUGIN,
                    subPlatform: userSettings.platform,
                } });
            self.editContact(data, isRevert);
        }
        self.setContactKeyBeingEdited(null);
        self.reportContactEditedEvent({
            isRevert,
            itemType,
            contactId,
            isBulk,
            isWishlist: contact.isWishlistContact,
        });
    },
});
